import React, { useEffect, useState } from 'react';
import { categoriesAPI } from '../services/api';
import { Category } from '../types';
import { useAuth } from '../contexts/AuthContext';
import './Categories.css';

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [showDeleted, setShowDeleted] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchCategories();
  }, [showDeleted]);

  const fetchCategories = async () => {
    try {
      const data = await categoriesAPI.getAll({ withDeleted: showDeleted });
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editing) {
        await categoriesAPI.update(editing.id, formData);
      } else {
        await categoriesAPI.create(formData);
      }
      setShowForm(false);
      setEditing(null);
      setFormData({ name: '', description: '' });
      fetchCategories();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при сохранении');
    }
  };

  const handleEdit = (category: Category) => {
    setEditing(category);
    setFormData({ name: category.name, description: category.description || '' });
    setShowForm(true);
  };

  const handleDelete = async (category: Category) => {
    if (!window.confirm(`Удалить категорию "${category.name}"?`)) return;
    try {
      await categoriesAPI.delete(category.id);
      fetchCategories();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при удалении');
    }
  };

  const handleRestore = async (category: Category) => {
    if (!window.confirm(`Восстановить категорию "${category.name}"?`)) return;
    try {
      await categoriesAPI.restore(category.id);
      fetchCategories();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при восстановлении');
    }
  };

  if (loading) {
    return <div>Загрузка...</div>;
  }

  return (
    <div className="categories">
      <div className="page-header">
        <h1>Категории</h1>
        <div className="page-header-actions">
          {isAdmin() && (
            <button
              className={`btn-toggle-deleted ${showDeleted ? 'active' : ''}`}
              onClick={() => setShowDeleted(!showDeleted)}
            >
              🗑 {showDeleted ? 'Скрыть удалённые' : 'Показать удалённые'}
            </button>
          )}
          {isAdmin() && (
            <button onClick={() => setShowForm(!showForm)} className="btn-primary">
              {showForm ? 'Отмена' : '+ Добавить категорию'}
            </button>
          )}
        </div>
      </div>

      {showForm && isAdmin() && (
        <div className="form-card">
          <h2>{editing ? 'Редактировать категорию' : 'Новая категория'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Название</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Описание</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <button type="submit" className="btn-primary">
              {editing ? 'Сохранить' : 'Создать'}
            </button>
          </form>
        </div>
      )}

      {/* Desktop Categories Table */}
      <div className="table-container categories-desktop-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Описание</th>
              {isAdmin() && <th>Действия</th>}
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category.id} className={category.deletedAt ? 'row-deleted' : ''}>
                <td>{category.id}</td>
                <td>{category.name}</td>
                <td>{category.description || '-'}</td>
                {isAdmin() && (
                  <td>
                    <div className="action-buttons-cell">
                      {!category.deletedAt && (
                        <button onClick={() => handleEdit(category)} className="btn-edit">
                          Изменить
                        </button>
                      )}
                      {category.deletedAt ? (
                        <button onClick={() => handleRestore(category)} className="btn-restore">
                          Восстановить
                        </button>
                      ) : (
                        <button onClick={() => handleDelete(category)} className="btn-delete">
                          Удалить
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Categories Cards */}
      <div className="categories-mobile-list">
        {categories.length === 0 ? (
          <div className="mobile-empty-card">Категории не найдены</div>
        ) : (
          categories.map((category) => (
            <div key={category.id} className={`mobile-category-card ${category.deletedAt ? 'card-deleted' : ''}`}>
              <div className="mobile-card-head">
                <div className="mobile-cat-title-col">
                  <span className="mobile-cat-id">#{category.id}</span>
                  <span className="mobile-cat-name">{category.name}</span>
                </div>
                {category.deletedAt && (
                  <span className="mobile-deleted-pill">Удалена</span>
                )}
              </div>

              {category.description && (
                <div className="mobile-cat-desc">
                  {category.description}
                </div>
              )}

              {isAdmin() && (
                <div className="mobile-card-footer-actions">
                  {!category.deletedAt && (
                    <button onClick={() => handleEdit(category)} className="btn-edit">
                      Изменить
                    </button>
                  )}
                  {category.deletedAt ? (
                    <button onClick={() => handleRestore(category)} className="btn-restore">
                      Восстановить
                    </button>
                  ) : (
                    <button onClick={() => handleDelete(category)} className="btn-delete">
                      Удалить
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Categories;
